import { EPSILON, MAX_EDGE_TRAVERSALS } from './constants'
import { getEdge, getVehicleById } from './queries'
import type { Network, Vehicle } from './types'

function getMovementShortfall(requested: number, actual: number): number {
  return Math.max(0, Math.abs(requested) - Math.abs(actual))
}

/**
 * Moves a vehicle along the track graph by `distance` units, traversing edge
 * boundaries as needed. Returns the actual distance moved (may be less than
 * requested if the vehicle hits a dead-end or bumper).
 */
export function moveVehicleBy(network: Network, vehicle: Vehicle, distance: number): number {
  if (!Number.isFinite(distance)) return 0

  let remaining = distance
  let actualMoved = 0
  let traversals = 0

  while (Math.abs(remaining) > EPSILON && traversals++ < MAX_EDGE_TRAVERSALS) {
    const edge = getEdge(network, vehicle.edgeId)
    if (!edge) break

    // Keep t on the edge so a bad spawn or float drift cannot produce negative boundary distances.
    if (!(vehicle.t >= 0)) vehicle.t = 0
    else if (vehicle.t > edge.length) vehicle.t = edge.length

    const isForward = remaining > 0
    const distanceToBoundary = isForward ? edge.length - vehicle.t : vehicle.t

    if (Math.abs(remaining) < distanceToBoundary) {
      vehicle.t += remaining
      actualMoved += remaining
      break
    }

    const stepToBoundary = isForward ? distanceToBoundary : -distanceToBoundary
    remaining -= stepToBoundary
    actualMoved += stepToBoundary

    const arrivedPortId = isForward === (vehicle.direction === 1) ? edge.b : edge.a
    const adjacency = network.adjacency.get(arrivedPortId) || []
    const next = adjacency.find(n => n.edgeId !== vehicle.edgeId)

    if (!next) break

    const nextEdge = getEdge(network, next.edgeId)
    if (!nextEdge) break

    vehicle.edgeId = next.edgeId

    if (isForward) {
      vehicle.direction = nextEdge.a === arrivedPortId ? 1 : -1
      vehicle.t = 0
    } else {
      vehicle.direction = nextEdge.b === arrivedPortId ? 1 : -1
      vehicle.t = nextEdge.length
    }
  }

  return actualMoved
}

/**
 * Returns the fraction [0, 1] by which all planned moves can be scaled without
 * any vehicle overshooting its edge boundary.
 */
const probeScratch: Vehicle = { id: '', type: 'wagon', edgeId: '', direction: 1, length: 0, t: 0 }

/** Copies the track placement of `source` into a reusable probe for dry-run moves. */
export function loadProbe(source: Vehicle, probe: Vehicle = probeScratch): Vehicle {
  probe.id = source.id
  probe.type = source.type
  probe.edgeId = source.edgeId
  probe.direction = source.direction
  probe.length = source.length
  probe.t = source.t
  return probe
}

export function getMovementScale(network: Network, moves: Map<Vehicle, number>): number {
  let scale = 1

  for (const [vehicle, distance] of moves.entries()) {
    if (Math.abs(distance) < EPSILON) continue

    const probe = loadProbe(vehicle)
    const actual = moveVehicleBy(network, probe, distance)
    const shortfall = getMovementShortfall(distance, actual)
    const ratio = Math.min(1, Math.abs(actual) / Math.abs(distance))

    if (shortfall > EPSILON) {
      //   console.log(
      //     'getMovementScale blocked:', vehicle.id,
      //     'edgeId:', vehicle.edgeId,
      //     'distance:', distance.toFixed(5),
      //     'actual:', actual.toFixed(5),
      //     'shortfall:', shortfall.toFixed(5),
      //     'ratio:', ratio.toFixed(4),
      //   )
      return 0
    }

    scale = Math.min(scale, ratio)
  }

  return scale
}

/**
 * BFS from the locomotive through coupling links, assigning each consist member
 * its planned movement distance. Front-front and back-back couplings invert direction.
 */
function buildConsistMovesFrom(
  network: Network,
  root: Vehicle,
  targetDistance: number,
  moves: Map<Vehicle, number>,
  queue: Vehicle[],
): Map<Vehicle, number> {
  moves.clear()
  moves.set(root, targetDistance)

  queue.length = 0
  queue.push(root)

  for (let index = 0; index < queue.length; index++) {
    const curr = queue[index]
    const currDistance = moves.get(curr)!

    for (let side = 0; side < 2; side++) {
      const link = side === 0 ? curr.linkedFront : curr.linkedBack
      if (!link) continue
      const nextV = getVehicleById(network, link)
      if (!nextV || moves.has(nextV)) continue

      const isCurrFront = side === 0
      const isNextFront = nextV.linkedFront === curr.id
      const sign = isCurrFront === isNextFront ? -1 : 1

      moves.set(nextV, currDistance * sign)
      queue.push(nextV)
    }
  }

  return moves
}

export function buildConsistMoves(
  network: Network,
  targetDistance: number,
  targetMoves?: Map<Vehicle, number>,
  queue?: Vehicle[],
): Map<Vehicle, number> {
  const loco = network.locomotive
  if (!loco) return new Map<Vehicle, number>()

  const moves = targetMoves ?? new Map<Vehicle, number>()
  const scratchQueue = queue ?? []
  return buildConsistMovesFrom(network, loco, targetDistance, moves, scratchQueue)
}

export function buildNPCConsistMoves(
  network: Network,
  npc: Vehicle,
  targetDistance: number,
  targetMoves?: Map<Vehicle, number>,
  queue?: Vehicle[],
): Map<Vehicle, number> {
  const moves = targetMoves ?? new Map<Vehicle, number>()
  const scratchQueue = queue ?? []
  return buildConsistMovesFrom(network, npc, targetDistance, moves, scratchQueue)
}

/** Applies all planned moves to live vehicle state, scaled by `scale`. */
export function applyMoves(network: Network, moves: Map<Vehicle, number>, scale: number) {
  for (const [vehicle, distance] of moves.entries()) {
    moveVehicleBy(network, vehicle, distance * scale)
  }
}
